import type { UIItemContainerKind } from '@sorrem-client/types';
import type { ItemMenuBuilder } from '@sorrem-client/ui/components';
import { resolveItemMenuEntries, toItemMenuItems } from '@sorrem-client/ui/shared/item-menu';
import { canMoveItem, toItemSlotRef } from '@sorrem-client/ui/shared/equipment-slots';
import { useGameplay } from '@sorrem-client/ui/features/PlayPage/gameplay-context';
import { useGameSelector } from '@sorrem-client/ui/hooks';
import { getStoredLoadout } from '@sorrem/shared';
import { useCallback } from 'react';

type ItemSelection = { itemId: string; container: UIItemContainerKind };
type ItemTarget = { container: UIItemContainerKind; index: number };

export interface RunItemTransfer {
    onMoveTo: (selection: ItemSelection, target: ItemTarget) => void;
    canMoveTo: (selection: ItemSelection, target: ItemTarget) => boolean;
    /** An item's right-click menu (#267): Equip or Unequip, Use, Drop, Details. */
    itemMenu: ItemMenuBuilder;
}

export function createMoveRequestId(): string {
    return crypto.randomUUID();
}

/**
 * Moving items between the run's carried slots and loadout: which moves are
 * allowed (equipment types, hands and named-item rules), and sending them to
 * the game, and each item's right-click menu. Storage is out of reach during
 * a run.
 */
export function useRunItemTransfer(): RunItemTransfer {
    const { bridge, requestInventoryMove, requestConsumeCrystal, requestDropItem } = useGameplay();
    const carriedItems = useGameSelector(bridge, (state) => state.player.carriedItems);
    const equipped = useGameSelector(bridge, (state) => getStoredLoadout(state.player));
    const carriedCapacity = useGameSelector(bridge, (state) => state.player.maxCarryCapacity);

    const onMoveTo = useCallback((selection: ItemSelection, target: ItemTarget): void => {
        if (selection.container === 'storage' || target.container === 'storage') {
            return;
        }

        const targetSlotRef = toItemSlotRef(target.container, target.index);
        const requestId = createMoveRequestId();
        void requestInventoryMove(requestId, selection.itemId, targetSlotRef);
    }, [requestInventoryMove]);

    const canMoveTo = useCallback((selection: ItemSelection, target: ItemTarget): boolean => (
        canMoveItem({ equipped, carriedItems, carriedCapacity }, selection, target)
    ), [carriedCapacity, carriedItems, equipped]);

    const itemMenu = useCallback<ItemMenuBuilder>((selection, { selectItem }) => {
        const entries = resolveItemMenuEntries({ equipped, carriedItems, carriedCapacity }, selection, { hasStorage: false, canTrade: false, canUse: true, canDrop: true });
        return toItemMenuItems(entries, {
            move: (target) => onMoveTo(selection, target),
            details: () => selectItem(selection.itemId, selection.container),
            use: () => requestConsumeCrystal(selection.itemId),
            drop: () => requestDropItem(selection.itemId),
        });
    }, [carriedCapacity, carriedItems, equipped, onMoveTo, requestConsumeCrystal, requestDropItem]);

    return { onMoveTo, canMoveTo, itemMenu };
}
